// Emoji for the chat's text box (README "Chat"): a small cozy set in a popup (the smiley button beside Send), and
// shortcodes: ":" and two letters list the emoji whose names start that way above the box, and a whole ":name:" turns
// into its emoji as it's typed or pasted. The set, the names and the matching are pure, so plain Node can test them
// (tests/emoji.test.mjs); createEmojiKit wires them to the chat's markup (site/js/app.js). They're only ever text: the
// worker sees the emoji themselves, and counts each code point towards SAY_MAX like any letter.

// Every emoji here is from Emoji 12 (2019) or before, so every system still in use draws it (not a box). Each has its
// names: the first is the one the list and the popup show (Slack's and GitHub's where they're friendly), the rest are
// searched too, and turn into it when typed whole (unless an earlier emoji has that name first, see CODES).
export const SECTIONS = [
  { id: 'feels', emoji: [
    ['😊', 'blush smile happy'], ['🙂', 'slightly_smiling_face smile'], ['😄', 'smile happy'], ['😁', 'grin'],
    ['😂', 'joy laugh lol tears'], ['🤣', 'rofl laugh lol'], ['😅', 'sweat_smile phew'], ['😉', 'wink'],
    ['😌', 'relieved calm peaceful'], ['😍', 'heart_eyes love'], ['🥰', 'smiling_face_with_three_hearts love adore'],
    ['😎', 'sunglasses cool'], ['🤗', 'hugs hug'], ['🤔', 'thinking hmm think'], ['😮', 'open_mouth wow surprised'],
    ['😴', 'sleeping sleep tired'], ['🥱', 'yawn yawning_face tired sleepy'], ['🥺', 'pleading_face please'],
    ['😢', 'cry sad tear'], ['😭', 'sob cry sad'], ['😔', 'pensive sad'], ['😇', 'innocent angel'],
    ['🥳', 'partying_face party'], ['👋', 'wave hello hi bye'], ['👍', 'thumbsup +1 yes ok like'], ['🙏', 'pray thanks please'],
    ['👏', 'clap applause'], ['🙌', 'raised_hands yay hooray'], ['✌️', 'v peace'], ['✨', 'sparkles magic'],
    ['🔥', 'fire lit'], ['💤', 'zzz sleep'], ['👀', 'eyes look'], ['🎉', 'tada party congrats'],
  ] },
  { id: 'hearts', emoji: [
    ['❤️', 'heart love red_heart'], ['🧡', 'orange_heart'], ['💛', 'yellow_heart'], ['💚', 'green_heart'],
    ['💙', 'blue_heart'], ['💜', 'purple_heart'], ['🤍', 'white_heart'], ['🖤', 'black_heart'],
    ['💖', 'sparkling_heart'], ['💕', 'two_hearts'], ['💔', 'broken_heart heartbreak'],
  ] },
  { id: 'sky', emoji: [
    ['🌙', 'moon crescent_moon night'], ['🌕', 'full_moon'], ['⭐', 'star'], ['🌟', 'glowing_star star2'],
    ['🌠', 'shooting_star stars wish'], ['🌌', 'milky_way galaxy space'], ['🪐', 'planet ringed_planet saturn'],
    ['☁️', 'cloud cloudy'], ['🌧️', 'rain cloud_with_rain rainy'], ['⛈️', 'storm cloud_with_lightning_and_rain thunder'],
    ['🌫️', 'fog foggy mist'], ['☔', 'umbrella rain'], ['❄️', 'snowflake snow winter'], ['⛄', 'snowman'],
    ['🍂', 'fallen_leaf autumn fall leaves'], ['🍁', 'maple_leaf autumn'], ['🌸', 'cherry_blossom sakura spring blossom'],
    ['🌷', 'tulip flower'], ['🌈', 'rainbow'], ['☀️', 'sunny sun'], ['🌊', 'ocean wave sea'],
  ] },
  { id: 'music', emoji: [
    ['🎧', 'headphones music listen'], ['🎵', 'musical_note note music'], ['🎶', 'notes music'],
    ['🎹', 'piano musical_keyboard keys'], ['🎷', 'saxophone sax jazz'], ['🎸', 'guitar'], ['🎺', 'trumpet'],
    ['🎻', 'violin'], ['🥁', 'drum drums'], ['🎤', 'microphone mic sing'], ['📻', 'radio'], ['💿', 'cd vinyl record album'],
  ] },
  { id: 'cozy', emoji: [
    ['☕', 'coffee cafe hot'], ['🍵', 'tea matcha'], ['🍷', 'wine_glass wine'], ['🍺', 'beer'], ['🍜', 'ramen noodles'],
    ['🍣', 'sushi'], ['🍕', 'pizza'], ['🥐', 'croissant'], ['🍩', 'doughnut donut'], ['🍪', 'cookie'], ['🍰', 'cake'],
    ['📚', 'books study read'], ['📖', 'book read'], ['📝', 'memo note write'], ['💻', 'computer laptop work'],
    ['🕯️', 'candle'], ['🛋️', 'couch couch_and_lamp sofa'], ['🧸', 'teddy_bear teddy'], ['🧣', 'scarf'],
    ['🌿', 'herb plant leaf'], ['🎮', 'video_game game'], ['⏳', 'hourglass hourglass_flowing_sand time'],
  ] },
  { id: 'animals', emoji: [   // the chat names' animals first (site/js/live/chat.js ANIMALS), where there's an emoji for one
    ['🦊', 'fox fox_face'], ['🦉', 'owl'], ['🐱', 'cat kitty'], ['🦦', 'otter'], ['🦋', 'butterfly moth'],
    ['🐇', 'hare rabbit2'], ['🐼', 'panda panda_face'], ['🐨', 'koala'], ['🦌', 'deer'],
    ['🐦', 'bird sparrow robin heron crow finch'], ['🐻', 'bear'], ['🐸', 'frog'], ['🐌', 'snail'], ['🦝', 'raccoon'],
    ['🦡', 'badger'], ['🐳', 'whale'], ['🐢', 'turtle'], ['🦔', 'hedgehog'], ['🦇', 'bat'], ['🦢', 'swan'],
    ['🐧', 'penguin'], ['🦆', 'duck'], ['🐺', 'wolf'], ['🐰', 'bunny rabbit'], ['🐤', 'baby_chick chick'],
    ['🐶', 'dog puppy'], ['🐾', 'paw_prints feet paws'],
  ] },
  { id: 'city', emoji: [
    ['🌃', 'night_with_stars city'], ['🌆', 'city_sunset dusk evening'], ['🌉', 'bridge_at_night bridge'],
    ['🏙️', 'cityscape skyline'], ['🗼', 'tokyo_tower tokyo'], ['🗽', 'statue_of_liberty new_york'],
    ['🏮', 'lantern izakaya_lantern'], ['🕌', 'mosque'], ['🏛️', 'classical_building'], ['🏰', 'castle european_castle'],
    ['🎡', 'ferris_wheel'], ['🎄', 'christmas_tree christmas'], ['🎆', 'fireworks'], ['🚋', 'tram train'],
    ['🚲', 'bike bicycle'], ['⛵', 'sailboat boat'], ['⚓', 'anchor harbour'], ['🏖️', 'beach beach_umbrella'],
  ] },
];

/** Every emoji of the set, in the popup's order: { e, names, section }. */
export const EMOJI = SECTIONS.flatMap(s => s.emoji.map(([e, names]) => ({ e, names: names.split(' '), section: s.id })));
const BY_EMOJI = new Map(EMOJI.map(x => [x.e, x]));

/** What a whole ":name:" turns into: each emoji's first name, then the others, the first emoji to have one keeping it. */
export const CODES = new Map();
for (const x of EMOJI) if (!CODES.has(x.names[0])) CODES.set(x.names[0], x.e);
for (const x of EMOJI) for (const n of x.names.slice(1)) if (!CODES.has(n)) CODES.set(n, x.e);

/** The name an emoji of the set goes by (':heart:' shows 'heart'), or null. */
export const nameOfEmoji = e => BY_EMOJI.get(e)?.names[0] ?? null;

// A shortcode's colon starts a word (not "10:30", "re:ok" or "https://"), and a name is letters, digits and _ + -.
const CODE = /(^|[^\p{L}\p{N}:_])(:([a-z0-9_+-]+):)/giu;
const TYPING = /(?:^|[^\p{L}\p{N}:_]):([a-z][a-z0-9_+-]*)$/iu;
export const QUERY_MIN = 2;                 // letters after the colon before the list shows (":p" stays a face)
export const SUGGEST_MAX = 6;

/** The shortcode being typed just before `caret`: { start, query } (start: its colon) for ":" and 2+ letters, or null. */
export function shortcodeAt(text, caret) {
  const m = TYPING.exec(text.slice(0, caret));
  if (!m || m[1].length < QUERY_MIN) return null;
  return { start: caret - m[1].length - 1, query: m[1].toLowerCase() };
}

// The ones people use most, most first: among names that match a query equally well, these lead (":he" is ❤️ before
// 🌿 herb, ":co" is ☕ before 🛋️ couch).
const TOP = ['❤️', '😂', '😭', '🥺', '🔥', '✨', '🙏', '👍', '😊', '😄', '🥰', '😍', '🎉', '👋', '😴', '☕', '🎧', '🌙', '🌧️',
  '❄️', '💤', '😌', '🎶', '⭐', '🤔', '👀', '💖', '😎', '🍵', '🌸'];
const topRank = e => (TOP.includes(e) ? TOP.indexOf(e) : TOP.length);

/**
 * The emoji of the set whose names match `query`, best first, SUGGEST_MAX at most: a name that is the query, then one
 * that starts with it, then one with a word that does ("moon" finds crescent_moon); an emoji's first name before its
 * others, then the most used (TOP), then the shorter name (":hea" is heart before heart_eyes), then the popup's order.
 */
export function emojiMatches(query, max = SUGGEST_MAX) {
  const q = query.toLowerCase();
  if (!q) return [];
  const scored = [];
  EMOJI.forEach((x, i) => {
    let best = null;
    x.names.forEach((n, j) => {
      const tier = n === q ? 0 : n.startsWith(q) ? 1 : n.split(/[_-]/).some(w => w.startsWith(q)) ? 2 : null;
      if (tier === null) return;
      const score = tier * 2 + (j > 0 ? 1 : 0);
      if (!best || score < best.score || (score === best.score && n.length < best.length)) best = { score, length: n.length };
    });
    if (best) scored.push({ x, ...best, top: topRank(x.e), i });
  });
  return scored.sort((a, b) => a.score - b.score || a.top - b.top || a.length - b.length || a.i - b.i).slice(0, max).map(s => s.x);
}

/**
 * `text` with every whole ":name:" of CODES as its emoji: { text, caret (where `caret` ends up), added (the emoji, in
 * order) }. Codes back to back (":heart::fire:") take a pass each, as the colon between them only starts the second
 * once the first is an emoji.
 */
export function withShortcodes(text, caret = text.length) {
  const added = [];
  for (let changed = true; changed;) {
    let shift = 0;
    changed = false;
    text = text.replace(CODE, (whole, before, code, name, at) => {
      const e = CODES.get(name.toLowerCase());
      if (!e) return whole;
      const from = at + before.length;
      if (caret >= from + code.length) shift += e.length - code.length;
      else if (caret > from) shift += from + e.length - caret;   // inside the code: after its emoji
      added.push(e);
      changed = true;
      return before + e;
    });
    caret += shift;
  }
  return { text, caret, added };
}

export const RECENT_MAX = 8;
/** The recently used emoji with `e` first (or without, `e` null): set members only, each once, `max` at most. */
export const withRecent = (list, e, max = RECENT_MAX) =>
  [e, ...(Array.isArray(list) ? list : [])].filter((x, i, all) => BY_EMOJI.has(x) && all.indexOf(x) === i).slice(0, max);

/** How many emoji `text` has, any emoji (not only the set's), a joined family, a skin tone or a flag counting as one. */
export const emojiCount = text =>
  (text.match(/\p{Regional_Indicator}{2}|(?:\p{Emoji_Presentation}|\p{Extended_Pictographic}\uFE0F)(?:\p{Emoji_Modifier}|\uFE0F|\u200D\p{Extended_Pictographic}\uFE0F?)*/gu) ?? []).length;

/* ---------- the chat box's emoji (site/js/app.js) ---------- */
/**
 * Wires the set to the chat's text box `input`: the popup `picker` opened by `btn`, and the shortcode list `suggest`
 * (a listbox; `input` is its combobox). `recent` is what was used last (the popup shows it on top) and `onRecent(list)`
 * keeps it; `onChange()` runs after the kit changes the text (the page's counter), `onAdd(via, e)` after each emoji
 * it adds (via 'picker', 'suggest' or 'shortcode'), and `onToggle(open)` as the popup opens and closes. On touch
 * screens the popup takes the place of the keyboard: it lowers it, and a tap on the text box closes the popup again.
 * Returns { keydown(e): true when the list or the popup took the key (the page's own Escape must wait), close() }.
 */
export function createEmojiKit({ input, btn, picker, suggest, touch = false, recent = [], onRecent, onChange, onAdd, onToggle }) {
  let recents = withRecent(recent, null);
  let grid = null, hint = null, recentSec = null, matches = [], active = 0, dismissed = null;
  const isOpen = () => !picker.hidden;
  const suggesting = () => !suggest.hidden;

  function used(e, via) {
    recents = withRecent(recents, e);
    onRecent?.(recents);
    onAdd?.(via, e);
  }
  // In the box, where its caret (or selection) is; the box keeps its focus, or stays without it on a touch screen.
  function put(text, start = input.selectionStart ?? input.value.length, end = input.selectionEnd ?? start) {
    input.setRangeText(text, start, end, 'end');
    onChange?.();
  }

  /* the popup: a grid of buttons by section, the recent ones on top, and a line naming the one under the pointer */
  const button = e => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'emoji';
    b.tabIndex = -1;
    b.textContent = e;
    b.dataset.e = e;
    b.setAttribute('aria-label', nameOfEmoji(e).replaceAll('_', ' '));
    return b;
  };
  function section(id, list) {
    const sec = document.createElement('div'), label = document.createElement('p'), row = document.createElement('div');
    sec.className = 'emoji-sec';
    sec.setAttribute('role', 'group');
    sec.setAttribute('aria-label', id);
    label.className = 'emoji-label';
    label.textContent = id;
    label.setAttribute('aria-hidden', 'true');
    row.className = 'emoji-row';
    row.append(...list.map(button));
    sec.append(label, row);
    return sec;
  }
  function build() {
    grid = document.createElement('div');
    grid.className = 'emoji-scroll';
    recentSec = section('recent', []);
    grid.append(recentSec, ...SECTIONS.map(s => section(s.id, s.emoji.map(([e]) => e))));
    hint = document.createElement('p');
    hint.className = 'emoji-hint';
    hint.setAttribute('aria-hidden', 'true');
    picker.prepend(grid, hint);                       // keep the shortcode reference link below the grid
    grid.addEventListener('mousedown', e => {           // the text box keeps its focus (and a phone its keyboard down)
      if (e.target.closest?.('.emoji')) e.preventDefault();
    });
    grid.addEventListener('click', e => {
      const b = e.target.closest?.('.emoji');
      if (!b) return;
      put(b.dataset.e);
      used(b.dataset.e, 'picker');
      rove(b);
    });
    grid.addEventListener('pointerover', e => showHint(e.target.closest?.('.emoji')));
    grid.addEventListener('pointerleave', () => showHint(grid.contains(document.activeElement) ? document.activeElement : null));
    grid.addEventListener('focusin', e => showHint(e.target.closest?.('.emoji')));
    grid.addEventListener('keydown', gridKeys);
  }
  function showHint(b) {
    hint.textContent = b?.dataset.e ? `${b.dataset.e} :${nameOfEmoji(b.dataset.e)}:` : 'or type : and a name, like :coffee:';
  }
  const buttons = () => [...grid.querySelectorAll('.emoji')].filter(b => !b.closest('[hidden]'));
  function rove(b) {                                  // one button in the tab order: the last one used, or the first
    for (const x of grid.querySelectorAll('.emoji[tabindex="0"]')) x.tabIndex = -1;
    b.tabIndex = 0;
  }
  // Arrows move through the grid as it's drawn (a row down is the nearest button in the next row, across sections),
  // Escape goes back to the text box. The keys the page uses elsewhere (arrows, Space) stay here.
  function gridKeys(e) {
    const all = buttons(), i = all.indexOf(document.activeElement);
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); input.focus(); return; }
    if (e.key === ' ' || e.key === 'Enter') { e.stopPropagation(); return; }   // the button's own click, not play/pause
    if (i < 0) return;
    let to = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: all.length - 1 }[e.key];
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') to = rowStep(all, i, e.key === 'ArrowDown' ? 1 : -1);
    if (to === undefined) return;
    e.preventDefault();
    e.stopPropagation();
    const b = all[Math.max(0, Math.min(all.length - 1, to))];
    rove(b);
    b.focus({ preventScroll: true });
    b.scrollIntoView({ block: 'nearest' });
  }
  function rowStep(all, i, dir) {
    const at = all[i].getBoundingClientRect(), mid = at.left + at.width / 2;
    const rows = [...new Set(all.map(b => Math.round(b.getBoundingClientRect().top)))].sort((a, b) => a - b);
    const next = rows[rows.indexOf(Math.round(at.top)) + dir];
    if (next === undefined) return i;
    let best = i, gap = Infinity;
    all.forEach((b, j) => {
      const r = b.getBoundingClientRect();
      if (Math.round(r.top) !== next) return;
      const d = Math.abs(r.left + r.width / 2 - mid);
      if (d < gap) { gap = d; best = j; }
    });
    return best;
  }

  function open(trigger) {
    closeSuggest();
    if (!grid) build();
    recentSec.hidden = !recents.length;
    recentSec.querySelector('.emoji-row').replaceChildren(...recents.map(button));
    showHint(null);
    picker.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    grid.scrollTop = 0;
    const first = buttons()[0];
    rove(first);
    onToggle?.(true);
    if (trigger === 'keyboard') first.focus({ preventScroll: true });
    else if (touch) input.blur();                     // the popup is the keyboard now
  }
  function close() {
    closeSuggest();
    if (!isOpen()) return;
    const hadFocus = picker.contains(document.activeElement);
    picker.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    onToggle?.(false);
    if (hadFocus) input.focus({ preventScroll: true });
  }
  btn.addEventListener('mousedown', e => e.preventDefault());   // as for the grid: the text box keeps its focus
  btn.addEventListener('click', e => (isOpen() ? close() : open(e.detail === 0 ? 'keyboard' : 'pointer')));
  btn.addEventListener('keydown', e => {              // its Escape closes the popup, not the whole chat
    if (e.key === 'Escape' && isOpen()) { e.preventDefault(); e.stopPropagation(); close(); }
  });
  input.addEventListener('focus', () => { if (touch && isOpen()) close(); });   // a tap on the box: the keyboard is back

  /* shortcodes: the list above the box while one is typed, and whole ones turned into emoji */
  function refresh() {
    const at = shortcodeAt(input.value, input.selectionStart ?? input.value.length);
    const key = at && `${at.start}:${at.query}`;
    matches = at && key !== dismissed ? emojiMatches(at.query) : [];
    if (!matches.length) { closeSuggest(); return; }
    active = 0;
    suggest.replaceChildren(...matches.map((x, i) => {
      const li = document.createElement('li'), e = document.createElement('span'), name = document.createElement('span');
      li.id = `${suggest.id}-${i}`;
      li.setAttribute('role', 'option');
      e.className = 'emoji-e';
      e.textContent = x.e;
      name.textContent = `:${x.names[0]}:`;
      li.append(e, name);
      return li;
    }));
    suggest.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    mark();
  }
  function mark() {
    [...suggest.children].forEach((li, i) => li.setAttribute('aria-selected', String(i === active)));
    input.setAttribute('aria-activedescendant', `${suggest.id}-${active}`);
  }
  function closeSuggest() {
    if (!suggesting()) return;
    suggest.hidden = true;
    suggest.replaceChildren();
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  }
  function pick(i) {
    const x = matches[i], caret = input.selectionStart ?? input.value.length, at = shortcodeAt(input.value, caret);
    if (!x || !at) return;
    put(input.value[caret] === ' ' ? x.e : `${x.e} `, at.start, caret);
    closeSuggest();
    used(x.e, 'suggest');
  }
  suggest.addEventListener('mousedown', e => e.preventDefault());
  suggest.addEventListener('click', e => {
    const li = e.target.closest?.('li');
    if (li) pick([...suggest.children].indexOf(li));
  });
  input.addEventListener('input', e => {
    if (e.isComposing) return;                        // an IME still at work: when it's done
    if (e.inputType?.startsWith('insert') || e.inputType === undefined) convert();
    refresh();
  });
  input.addEventListener('compositionend', () => { convert(); refresh(); });
  input.addEventListener('blur', closeSuggest);
  function convert() {
    const { text, caret, added } = withShortcodes(input.value, input.selectionStart ?? input.value.length);
    if (!added.length) return;
    input.value = text;
    input.setSelectionRange(caret, caret);
    onChange?.();
    for (const e of added) used(e, 'shortcode');
  }

  function keydown(e) {
    if (e.isComposing || e.keyCode === 229) return false;
    if (suggesting()) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        active = (active + (e.key === 'ArrowDown' ? 1 : -1) + matches.length) % matches.length;
        mark();
        return true;
      }
      if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) { e.preventDefault(); pick(active); return true; }   // not the form's Enter
      if (e.key === 'Escape') {                       // until the code changes: typing on shows it again
        e.preventDefault();
        const at = shortcodeAt(input.value, input.selectionStart ?? input.value.length);
        dismissed = at && `${at.start}:${at.query}`;
        closeSuggest();
        return true;
      }
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) closeSuggest();
    }
    if (e.key === 'Escape' && isOpen()) { e.preventDefault(); close(); return true; }
    return false;
  }

  return { keydown, close };
}
